// YouTubeAffiliateTracker.vue
<template>
  <div class="container">
    <h1 style="font-family: 'Franklin Gothic Medium', 'Arial Narrow', Arial, sans-serif;">backr.ai - get deals while supporting your favorite YouTubers</h1>

    <!-- Search Form -->
    <div class="search-form">
      <form @submit.prevent="searchCreator" class="flex gap-4">
        <input id="creator-search" v-model="searchQuery" type="text" placeholder="Enter a YouTube creator's name..."
          class="flex-1 rounded-md" />
        <button type="submit" :disabled="isLoading">
          {{ isLoading ? 'Searching...' : 'Search' }}
        </button>
      </form>
    </div>

    <!-- Error Message -->
    <div v-if="error" class="error">
      {{ error }}
    </div>

    <!-- Previous Searches -->
    <div v-if="previousSearches.length" class="mb-8">
      <h2 class="text-xl font-semibold mb-4">Previous Searches</h2>
      <div class="previous-searches">
        <div v-for="creator in previousSearches" :key="creator.handle" class="creator-card">
          <div class="creator-card-content cursor-pointer" @click="loadCreator(creator)">
            <h3 class="font-bold creator-handle">{{ creator.handle }}</h3>
            <p class="text-sm text-gray-600">
              Last updated: {{ new Date(creator.lastUpdated).toLocaleDateString() }}
            </p>
            <p class="text-sm">
              {{ creator.affiliateLinks.length }} promo codes found
            </p>
          </div>

          <button @click.stop="removeCreator(creator.handle)" class="creator-remove-btn" title="Remove this creator">
            ×
          </button>
        </div>
      </div>
    </div>

    <!-- Current Creator Results -->
    <div v-if="currentCreator" id="search-results" class="results-container relative">

      <h2 class="text-xl font-semibold mb-4">
        Products mentioned by {{ currentCreator.handle }}
      </h2>

      <div class="results-grid">
        <div v-for="(product, index) in currentCreator.affiliateLinks" :key="index" class="result-card">
          <!-- Product Name and Discount -->
          <div class="card-header">
            <h3 class="product-title">

              <!--<a :href="product.url" target="_blank">-->{{ product.productName }}
              <br>
              <a :href="product.url" target="_blank" class="product-link">
                {{ product.url }}
              </a>
            </h3>

            <span v-if="product.percentOff" class="discount-tag">
              {{ product.percentOff }}% off
            </span>
          </div>

          <!-- Tags -->
          <div class="tags">
            <span class="company-tag">{{ product.company }}</span>
            <span class="type-tag">{{ product.productType }}</span>
          </div>

          <!-- Discount Code if available -->
          <!--<div v-if="product.discountCode" class="promo-code">
            Code: <span class="code">{{ product.discountCode }}</span>-
          </div>-->

          <!-- Source -->
          <div class="source-info">
            <a :href="`https://youtube.com/watch?v=${product.videoInfo?.id}`" target="_blank" class="video-link">
              from {{ product.videoInfo?.title }}
            </a>
            <span class="date">posted {{ product.videoInfo?.publishedAt }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Search Progress -->
    <div v-if="searchProgress.stage" class="search-progress-container">
      <div class="progress-content">
        <div class="progress-header">
          <span class="stage-indicator" :class="searchProgress.stage">
            {{ searchProgress.stage === 'searching' ? '🔍' :
            searchProgress.stage === 'analyzing' ? '🤖' :
            searchProgress.stage === 'complete' ? '✅' : '❌' }}
            {{ searchProgress.stage.charAt(0).toUpperCase() + searchProgress.stage.slice(1) }}
          </span>
          <span class="progress-percent">{{ Math.round(searchProgress.percent) }}%</span>
        </div>

        <div class="progress-bar-container">
          <div class="progress-bar" :style="{ width: `${searchProgress.percent}%` }" :class="searchProgress.stage">
          </div>
        </div>

        <div class="progress-message">{{ searchProgress.message }}</div>

        <div v-if="searchProgress.stage === 'searching'" class="progress-details">
          Video {{ searchProgress.currentVideo }} of {{ searchProgress.totalVideos }}
        </div>
        <div v-if="searchProgress.stage === 'analyzing'" class="progress-details">
          Batch {{ searchProgress.currentChunk }} of {{ searchProgress.totalChunks }}
        </div>
      </div>
    </div>

    <!-- Add data management buttons -->
    <div class="data-controls">
      <button @click="downloadSearchData" class="data-button">
        Download Search Data
      </button>
      <input
        type="file"
        ref="fileInput"
        @change="handleFileUpload"
        accept=".json"
        style="display: none"
      />
      <button @click="triggerFileUpload" class="data-button">
        Import Search Data
      </button>
    </div>
  </div>
</template>

<script>
import Groq from "groq-sdk";

// Utility function to extract affiliate links from description
function extractAllLinks(description) {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const links = [];
  let match;
  
  while ((match = urlRegex.exec(description)) !== null) {
    // Get context before and after the URL
    const contextStart = Math.max(0, match.index - 150);
    const contextEnd = Math.min(description.length, match.index + match[0].length + 150);
    const beforeContext = description.slice(contextStart, match.index).trim();
    const afterContext = description.slice(match.index + match[0].length, contextEnd).trim();
    
    links.push({
      url: match[0],
      beforeContext,
      afterContext
    });
  }
  
  return links;
}

function extractAffiliateLinksUseCode(description) {
  const useCodeRegex = /(?:.*?to\s+.*?)?\buse\s+code\s+([\w\d-]+)(?:\s+.*?(?:to|at|for|on|when)\s+.*?)?/gi;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const links = [];
  
  const codeMatches = [...description.matchAll(useCodeRegex)];
  
  let match;
  while ((match = urlRegex.exec(description)) !== null) {
    const urlPosition = match.index;
    
    const nearbyCode = codeMatches.find(codeMatch => {
      const distance = Math.abs(codeMatch.index - urlPosition);
      return distance <= 200;
    });
    
    if (nearbyCode) {
      const promoCode = nearbyCode[1];
      
      // Find the sentence containing both the URL and promo code
      const sentences = description.split(/[.!?\n]+/);
      const containingSentence = sentences.find(sentence => 
        sentence.includes(match[0]) && sentence.includes(nearbyCode[0])
      ) || nearbyCode[0];
      
      let context = containingSentence.trim();
      
      // Add ellipsis if needed
      if (!context.startsWith(description.slice(0, 50))) context = '...' + context;
      if (!context.endsWith(description.slice(-50))) context += '...';
      
      links.push({
        url: match[0],
        context: context,
        promoCode: promoCode,
        isPromoCode: true
      });
    }
  }
  
  return links;
}

export default {
  name: 'YouTubeAffiliateTracker',
  
  data() {
    return {
      searchQuery: '',
      isLoading: false,
      error: null,
      previousSearches: [],
      currentCreator: null,
      apiKey: process.env.VUE_APP_YOUTUBE_API_KEY,
      groqApiKey: process.env.VUE_APP_GROQ_API_KEY,
      searchProgress: {
        stage: '', // 'searching', 'analyzing', 'complete'
        percent: 0,
        currentVideo: 0,
        totalVideos: 0,
        currentChunk: 0,
        totalChunks: 0,
        message: ''
      }
    }
  },

  async created() {
    // Initialize Groq client
    this.groq = new Groq({
      apiKey: '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',
      dangerouslyAllowBrowser: true
    });
    
    await this.loadInitialData();
  },

  methods: {
    extractAllLinks(description) {
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const links = [];
      let match;
      
      while ((match = urlRegex.exec(description)) !== null) {
        const contextStart = Math.max(0, match.index - 150);
        const contextEnd = Math.min(description.length, match.index + match[0].length + 150);
        const beforeContext = description.slice(contextStart, match.index).trim();
        const afterContext = description.slice(match.index + match[0].length, contextEnd).trim();
        
        links.push({
          url: match[0],
          beforeContext,
          afterContext
        });
      }
      
      return links;
    },

    // Helper function to chunk array into smaller pieces
    chunkArray(array, size) {
      const chunks = [];
      for (let i = 0; i < array.length; i += size) {
        chunks.push(array.slice(i, i + size));
      }
      return chunks;
    },

    async analyzeLinksWithGroq(videos, youtuber) {
      // First, flatten all links with their video context
      const linksData = videos.flatMap(video => 
        video.links.map(link => ({
          url: link.url,
          beforeContext: link.beforeContext,
          afterContext: link.afterContext,
          videoTitle: video.videoTitle,
          videoId: video.videoId,
          publishedAt: video.publishedAt
        }))
      );

      // Chunk the links into smaller groups (35 links per chunk)
      const chunks = this.chunkArray(linksData, 15);
      const allAnalyzedLinks = [];
      
      this.searchProgress.totalChunks = chunks.length;
      const baseProgress = 50; // Starting progress for analysis phase
      const progressPerChunk = 40 / chunks.length; // Distribute 40% across all chunks

      try {
        for (let i = 0; i < chunks.length; i++) {
          const chunk = chunks[i];
          this.searchProgress.currentChunk = i + 1;
          // Calculate current progress
          const currentProgress = baseProgress + (i * progressPerChunk);
          this.updateSearchProgress(
            'analyzing',
            currentProgress,
            `Analyzing link group ${i + 1} of ${chunks.length} (${chunk.length} links)`
          );

          console.log(`Processing chunk ${i + 1}/${chunks.length}, progress: ${currentProgress.toFixed(1)}%`);
          
          const prompt = `Analyze these ${chunk.length} URLs and their surrounding context. For each URL, extract key information and format as JSON.

Key identification rules:
1. Affiliate Link Indicators:
- Contains tracking parameters (ref=, affiliate=, partner=)
- URL includes influencer/creator name (${youtuber})
- Uses redirect/tracking domains (like linktr.ee, bit.ly, amzn.to)
- Has long query strings with tracking IDs
- Comes from known affiliate networks (Impact, Commission Junction, etc.)

For each URL extract:
- Product name: [from context or URL, fallback to company name]
- Company name: [merchant/brand name]
- Discount percentage: [number if mentioned]
- Discount code: [promo code if provided]
- Product type/category: [general product category]
- isAffiliate: [true if matches affiliate indicators above, false otherwise]

Return format:
{
  "links": [
    {
      "url": "string",
      "productName": "string", 
      "company": "string",
      "percentOff": number or null,
      "discountCode": "string" or null,
      "productType": "string",
      "isAffiliate": boolean
    }
  ]
}

Remove duplicates. Use null for missing fields.

Context: ${JSON.stringify(chunk)}
Creator: ${youtuber}`;

          const completion = await this.groq.chat.completions.create({
            messages: [{
              role: "user",
              content: prompt
            }],
            model: "llama-3.1-8b-instant",
            temperature: 0,
            max_tokens: 8000,
            response_format: { type: "json_object" }
          });

          console.log('Raw completion response:', completion.choices[0]?.message?.content);
          
          try {
            const parsedResponse = JSON.parse(completion.choices[0]?.message?.content || '{"links": []}');
            if (parsedResponse.links && Array.isArray(parsedResponse.links)) {
              allAnalyzedLinks.push(...parsedResponse.links);
            } else {
              console.error('Unexpected response structure:', parsedResponse);
            }
          } catch (parseError) {
            console.error('Error parsing chunk response:', parseError);
            console.error('Raw content:', completion.choices[0]?.message?.content);
            // Still update progress even if chunk fails
            this.updateSearchProgress(
              'analyzing',
              currentProgress + progressPerChunk,
              `Chunk ${i + 1} had errors, continuing...`
            );
          }

          // Add a small delay between chunks
          await new Promise(resolve => setTimeout(resolve, 200));
        }

        // Final progress update before returning
        this.updateSearchProgress(
          'analyzing',
          90, // Leave room for final processing
          'Finalizing analysis...'
        );

        // Recombine the analyzed data with the original videos structure
        return videos.map(video => ({
          ...video,
          links: video.links.map(link => {
            const analysis = allAnalyzedLinks.find(a => a.url === link.url) || {
              productName: null,
              company: null,
              percentOff: null,
              discountCode: null,
              productType: null,
              isAffiliate: null
            };
            return {
              ...link,
              ...analysis
            };
          }).filter((link) => link.isAffiliate === true)
        }));

      } catch (error) {
        console.error('Error analyzing links:', error);
        this.updateSearchProgress(
          'error',
          0,
          'Error during analysis: ' + error.message
        );
        return videos;
      }
    },

    // Helper method to safely convert to string and uppercase
    safeToUpperCase(value) {
      if (!value) return null;
      return String(value).toUpperCase();
    },

    deduplicateAndCleanProducts(videos) {
      // First, flatten all products with their video info
      const allProducts = videos.flatMap(video => 
        video.links.map(link => ({
          ...link,
          videoInfo: {
            title: video.videoTitle,
            id: video.videoId,
            publishedAt: video.publishedAt
          }
        }))
      );

      // Group by product name or URL if no name
      const productGroups = allProducts.reduce((groups, product) => {
        const key = product.productName || product.url;
        if (!groups[key]) {
          groups[key] = [];
        }
        groups[key].push(product);
        return groups;
      }, {});

      // Select best version of each product
      const uniqueProducts = Object.values(productGroups).map(group => {
        return group.reduce((best, current) => {
          // Score completeness (more fields = better)
          const currentScore = ['productName', 'company', 'percentOff', 'discountCode', 'productType']
            .filter(field => current[field]).length;
          const bestScore = ['productName', 'company', 'percentOff', 'discountCode', 'productType']
            .filter(field => best[field]).length;

          // Compare dates if scores are equal
          if (currentScore === bestScore) {
            return new Date(current.videoInfo.publishedAt) > new Date(best.videoInfo.publishedAt) 
              ? current : best;
          }
          return currentScore > bestScore ? current : best;
        });
      });

      // Clean up text formatting
      return uniqueProducts.map(product => ({
        ...product,
        productName: product.productName || product.productType || 'Unnamed Product',
        company: product.company || 'Unknown Company',
        productType: this.toTitleCase(product.productType || 'Other'),
        // Safely handle percentOff formatting
        percentOff: this.safeToUpperCase(product.percentOff),
        // Safely handle discountCode formatting
        discountCode: this.safeToUpperCase(product.discountCode)
      }));
    },

    // Helper method for title case conversion
    toTitleCase(str) {
      if (!str) return '';
      return String(str)
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },

    // Simplified validation that focuses on essential structure
    isValidStoredData(data) {
      return data && 
             data.handle && 
             Array.isArray(data.affiliateLinks);
    },

    // Helper to save to localStorage
    saveToLocalStorage(creatorData) {
      try {
        // Update/add to previous searches
        let previousSearches = [];
        try {
          const stored = localStorage.getItem('previousSearches');
          previousSearches = stored ? JSON.parse(stored) : [];
        } catch (e) {
          console.error('Error parsing previous searches:', e);
          previousSearches = [];
        }

        // Remove old entry for this creator if it exists
        const existingIndex = previousSearches.findIndex(c => c.handle === creatorData.handle);
        if (existingIndex !== -1) {
          previousSearches.splice(existingIndex, 1);
        }

        // Add new data at the start
        previousSearches.unshift(creatorData);
        
        // Keep only last 10 searches
        previousSearches = previousSearches.slice(0, 10);

        // Save back to localStorage
        localStorage.setItem('previousSearches', JSON.stringify(previousSearches));
        
        // Update component state
        this.previousSearches = previousSearches;
        console.log('Saved to localStorage:', creatorData.handle, 'Total searches:', previousSearches.length);
      } catch (e) {
        console.error('Error saving to localStorage:', e);
      }
    },

    updateSearchProgress(stage, percent, message) {
      this.searchProgress = {
        ...this.searchProgress,
        stage,
        percent,
        message
      };
    },

    // Update the searchCreator method
    async searchCreator() {
      this.isLoading = true;
      this.error = null;
      this.searchProgress = {
        stage: 'searching',
        percent: 0,
        currentVideo: 0,
        totalVideos: 0,
        currentChunk: 0,
        totalChunks: 0,
        message: 'Initializing search...'
      };

      try {
        // First get the channel handle
        const response = await fetch(`https://www.googleapis.com/youtube/v3/search?part=snippet&q=${encodeURIComponent(this.searchQuery)}&type=channel&key=${this.apiKey}`);
        const data = await response.json();
        
        if (!data.items?.length) {
          throw new Error('No channel found');
        }
        
        const channelId = data.items[0].id.channelId;
        const handle = `@${data.items[0].snippet.customUrl || data.items[0].snippet.channelTitle}`;
        
        // Get latest videos
        const numVideos = 4;
        const videosResponse = await fetch(`https://www.googleapis.com/youtube/v3/search?part=snippet&channelId=${channelId}&order=date&type=video&maxResults=${numVideos}&key=${this.apiKey}`);
        const videosData = await videosResponse.json();
        
        this.searchProgress.totalVideos = videosData.items.length;
        const affiliateLinks = [];

        // Process each video
        for (let i = 0; i < videosData.items.length; i++) {
          const item = videosData.items[i];
          this.searchProgress.currentVideo = i + 1;
          this.updateSearchProgress(
            'searching',
            20 + (i / videosData.items.length) * 30,
            `Analyzing video ${i + 1} of ${videosData.items.length}: ${item.snippet.title}`
          );

          const videoResponse = await fetch(`https://www.googleapis.com/youtube/v3/videos?part=snippet&id=${item.id.videoId}&key=${this.apiKey}`);
          const videoData = await videoResponse.json();
          
          const description = videoData.items[0].snippet.description;
          
          // Get both types of links
          const allLinks = this.extractAllLinks(description);
          const promoLinks = extractAffiliateLinksUseCode(description);
          
          // Combine all links, marking promo links appropriately
          const combinedLinks = [...allLinks, ...promoLinks];
          
          if (combinedLinks.length > 0) {
            affiliateLinks.push({
              videoTitle: item.snippet.title,
              videoId: item.id.videoId,
              publishedAt: new Date(item.snippet.publishedAt).toLocaleDateString(),
              links: combinedLinks
            });
          }
        }
        
        // Update progress for analysis phase
        this.updateSearchProgress('analyzing', 50, 'Starting AI analysis of links...');
        
        const analyzedLinks = await this.analyzeLinksWithGroq(affiliateLinks, handle);
        const uniqueProducts = this.deduplicateAndCleanProducts(analyzedLinks);
        
        const creatorData = {
          handle,
          lastUpdated: new Date().toISOString(),
          affiliateLinks: uniqueProducts
        };

        this.currentCreator = creatorData;
        this.saveToLocalStorage(creatorData);
        
        this.updateSearchProgress('complete', 100, 'Search complete!');

        // Wait for Vue to update the DOM
        await this.$nextTick();

        // Scroll to results
        const resultsElement = document.getElementById('search-results');
        if (resultsElement) {
          resultsElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
        
        // Clear progress after a delay
        setTimeout(() => {
          this.searchProgress.stage = '';
        }, 2000);

      } catch (err) {
        console.error('Search error:', err);
        this.error = `Error: ${err.message}`;
        this.updateSearchProgress('error', 0, `Error: ${err.message}`);
      } finally {
        this.isLoading = false;
      }
    },

    // Update loadCreator method
    async loadCreator(creator) {
      try {
        // Get fresh data from localStorage
        const stored = localStorage.getItem('previousSearches');
        if (stored) {
          const searches = JSON.parse(stored);
          const storedCreator = searches.find(c => c.handle === creator.handle);
          if (storedCreator && this.isValidStoredData(storedCreator)) {
            this.currentCreator = storedCreator;
            // Wait for Vue to update the DOM
            await this.$nextTick();

            // Scroll to results
            const resultsElement = document.getElementById('search-results');
            if (resultsElement) {
              resultsElement.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
            }
            return;
          }
        }
        
        // If we couldn't load from storage, do a fresh search
        this.searchQuery = creator.handle;
        await this.searchCreator();
      } catch (e) {
        console.error('Error loading creator:', e);
        // Do a fresh search as fallback
        this.searchQuery = creator.handle;
        await this.searchCreator();
      }
    },

    removeCreator(handle) {
      // Remove from current display if it's the active creator
      if (this.currentCreator && this.currentCreator.handle === handle) {
        this.currentCreator = null;
      }

      // Remove from previous searches
      this.previousSearches = this.previousSearches.filter(
        creator => creator.handle !== handle
      );

      // Update localStorage
      localStorage.setItem('previousSearches', JSON.stringify(this.previousSearches));
      
      console.log(`Removed creator: ${handle}`);
    },

    // Add these new methods
    downloadSearchData() {
      try {
        const data = {
          previousSearches: this.previousSearches,
          timestamp: new Date().toISOString(),
          version: '1.0'
        };
        
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        
        link.href = url;
        link.download = `backr-youtube-searches-${new Date().toISOString().slice(0,10)}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading data:', error);
        this.error = 'Failed to download search data';
      }
    },

    triggerFileUpload() {
      this.$refs.fileInput.click();
    },

    async handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      try {
        const text = await file.text();
        const data = JSON.parse(text);
        
        // Validate the imported data
        if (!Array.isArray(data.previousSearches)) {
          throw new Error('Invalid data format');
        }

        // Merge with existing searches, avoiding duplicates
        const existingIds = new Set(this.previousSearches.map(search => search.channelId));
        const newSearches = data.previousSearches.filter(search => !existingIds.has(search.channelId));
        
        this.previousSearches = [...this.previousSearches, ...newSearches];
        
        // Update localStorage
        localStorage.setItem('previousSearches', JSON.stringify(this.previousSearches));
        
        // Clear the file input
        event.target.value = '';
        
        // Show success message
        this.error = null;
        alert(`Successfully imported ${newSearches.length} new searches`);
      } catch (error) {
        console.error('Error importing data:', error);
        this.error = 'Failed to import search data: Invalid file format';
        event.target.value = '';
      }
    },

    async loadInitialData() {
      try {
        // First load from localStorage
        const storedData = this.loadFromLocalStorage();
        
        // Then load from JSON file
        const fileData = await this.loadFromFile();
        
        // Merge the data, giving preference to localStorage data
        if (fileData && Array.isArray(fileData.previousSearches)) {
          const existingIds = new Set(storedData.map(search => search.channelId));
          const newSearches = fileData.previousSearches.filter(search => !existingIds.has(search.channelId));
          this.previousSearches = [...storedData, ...newSearches];
          
          // Update localStorage with merged data
          localStorage.setItem('previousSearches', JSON.stringify(this.previousSearches));
        }
        
        console.log('Loaded searches:', this.previousSearches.length);
      } catch (error) {
        console.error('Error loading initial data:', error);
        // Fallback to just localStorage data if file loading fails
        this.previousSearches = this.loadFromLocalStorage();
      }
    },

    loadFromLocalStorage() {
      try {
        const stored = localStorage.getItem('previousSearches');
        if (stored) {
          const parsed = JSON.parse(stored);
          return Array.isArray(parsed) ? parsed.filter(this.isValidStoredData) : [];
        }
      } catch (e) {
        console.error('Error loading from localStorage:', e);
        localStorage.removeItem('previousSearches');
      }
      return [];
    },

    async loadFromFile() {
      try {
        const response = await fetch('/backr-youtube.json');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
      } catch (error) {
        console.warn('Could not load backr-youtube.json:', error);
        return null;
      }
    },
  }
}
</script>

<style>
.container {
  max-width: 56rem;
  margin: 0 auto;
  padding: 2rem 1rem;
}

h1 {
  font-size: 1.875rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 2rem;
}

.search-form {
  background-color: white;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-bottom: 2rem;
}

input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
}

input:focus {
  outline: none;
  border-color: #3B82F6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
}

button {
  background-color: #2563EB;
  color: white;
  font-weight: 500;
  padding: 0.5rem 1.5rem;
  border-radius: 0.375rem;
  transition: background-color 0.2s;
}

button:hover {
  background-color: #1D4ED8;
}

button:disabled {
  background-color: #93C5FD;
  cursor: not-allowed;
}

.error {
  background-color: #FEF2F2;
  color: #B91C1C;
  padding: 1rem;
  border-radius: 0.375rem;
  margin-bottom: 1.5rem;
}

.previous-searches {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  margin-bottom: 2rem;
}

.creator-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 1rem 1rem;
  margin-bottom: 0.75rem;
  transition: all 0.2s;
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.creator-card:hover {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}
.creator-handle {
  font-family:'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  color: #8900d2;
}

.creator-card-content {
  flex: 1;
  padding-right: 1rem;
}

.creator-remove-btn {
  font-size: 1.25rem;
  line-height: 1;
  color: #e0e043;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  transition: all 0.2s;
  font-weight: bold;
}

.creator-remove-btn:hover {
  color: #EF4444;
  background: #FEE2E2;
}

.affiliate-link {
  background-color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-bottom: 1rem;
}

.promo-code {
  background-color: #10B981;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 500;
}

.link-context {
  color: #4B5563;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  line-height: 1.5;
}

.flex {
  display: flex;
}

.gap-4 {
  gap: 1rem;
}

.flex-1 {
  flex: 1;
}

.rounded-md {
  border-radius: 0.375rem;
}

.text-xl {
  font-size: 1.25rem;
}

.font-semibold {
  font-weight: 600;
}

.mb-4 {
  margin-bottom: 1rem;
}

.mb-8 {
  margin-bottom: 2rem;
}

.font-bold {
  font-weight: 700;
}

.text-sm {
  font-size: 0.875rem;
}

.text-gray-600 {
  color: #4B5563;
}

.mb-2 {
  margin-bottom: 0.5rem;
}

.mt-2 {
  margin-top: 0.5rem;
}

.text-blue-500 {
  color: #3B82F6;
}

.block {
  display: block;
}

.break-all {
  word-break: break-all;
}

.space-y-4 > * + * {
  margin-top: 1rem;
}

.bg-blue-50 {
  background-color: #EFF6FF;
  border-radius: 0.5rem;
}

.affiliate-link {
  border-bottom: 1px solid #E5E7EB;
  padding-bottom: 1.5rem;
}

.affiliate-link:last-child {
  border-bottom: none;
}

.mx-2 {
  margin-left: 0.5rem;
  margin-right: 0.5rem;
}

.product-card {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.product-card:hover {
  transform: translateY(-2px);
  transition: transform 0.2s ease;
}

.results-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;
  position: relative;
}

.results-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
}

.result-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem;
  background: white;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.product-title {
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
  flex: 1;
}

.discount-tag {
  background: #dcfce7;
  color: #166534;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.875rem;
  white-space: nowrap;
}

.tags {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.company-tag,
.type-tag {
  font-size: 0.75rem;
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
}

.company-tag {
  background: #eff6ff;
  color: #1e40af;
}

.type-tag {
  background: #f3e8ff;
  color: #6b21a8;
}

.promo-code {
  font-size: 0.875rem;
  color: #4b5563;
  margin-bottom: 0.5rem;
}

.code {
  background: #f3f4f6;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  font-family: monospace;
}

.source-info {
  font-size: 0.875rem;
  border-top: 1px solid #e5e7eb;
  padding-top: 0.5rem;
  margin-top: 0.5rem;
}

.video-link {
  color: #2563eb;
  display: block;
  margin-bottom: 0.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.date {
  color: #6b7280;
  display: block;
  margin-bottom: 0.25rem;
}

.product-link {
  color: #2563eb;
  font-size: 0.875rem;
}

.video-link:hover,
.product-link:hover {
  text-decoration: underline;
}

.search-progress-container {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 400px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  overflow: hidden;
}

.progress-content {
  padding: 1rem;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.stage-indicator {
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.progress-percent {
  font-weight: 600;
  color: #4B5563;
}

.progress-bar-container {
  height: 6px;
  background: #E5E7EB;
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 0.75rem;
}

.progress-bar {
  height: 100%;
  transition: width 0.3s ease;
}

.progress-bar.searching {
  background: #3B82F6;
}

.progress-bar.analyzing {
  background: #8B5CF6;
}

.progress-bar.complete {
  background: #10B981;
}

.progress-bar.error {
  background: #EF4444;
}

.progress-message {
  font-size: 0.875rem;
  color: #4B5563;
  margin-bottom: 0.25rem;
}

.progress-details {
  font-size: 0.75rem;
  color: #6B7280;
}

.stage-indicator.searching { color: #3B82F6; }
.stage-indicator.analyzing { color: #8B5CF6; }
.stage-indicator.complete { color: #10B981; }
.stage-indicator.error { color: #EF4444; }

.remove-creator-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.5rem;
  color: #6B7280;
  border-radius: 9999px;
  transition: all 0.2s;
  background: #F3F4F6;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-creator-btn:hover {
  background: #EF4444;
  color: white;
}

.data-controls {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  justify-content: flex-end;
}

.data-button {
  padding: 8px 16px;
  background-color: #2196F3;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;
  transition: background-color 0.2s;
}

.data-button:hover {
  background-color: #1976D2;
}
</style>